import { useMemo, useState } from "react";
import { api, ApiError, type Intent } from "../api.ts";
import { keplrSignApproval } from "../keplr.ts";

type ActionKey = "gov.vote" | "bank.send" | "staking.delegate" | "distribution.withdrawRewards";
const ACTIONS: { id: ActionKey; label: string }[] = [
  { id: "bank.send", label: "💸 Send tokens" },
  { id: "staking.delegate", label: "🥩 Delegate (stake)" },
  { id: "gov.vote", label: "🗳️ Vote on a proposal" },
  { id: "distribution.withdrawRewards", label: "🎁 Withdraw rewards" },
];
const VOTE_OPTIONS = ["yes", "no", "abstain", "no_with_veto"] as const;
const STEPS = ["What to do", "Details", "Review", "Result"] as const;
const UATOM_PER_ATOM = 1_000_000;

function atomToUatom(atom: string): string | null {
  const t = atom.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * UATOM_PER_ATOM).toString();
}
function statusBadge(s: string): "ok" | "warn" | "bad" {
  if (s === "executed") return "ok";
  if (s === "awaiting_approval" || s === "authorized" || s === "awaiting_ack") return "warn";
  return "bad";
}

export function Intents() {
  const [step, setStep] = useState(0); // 0..3

  // what
  const [action, setAction] = useState<ActionKey>("bank.send");
  const [policyId, setPolicyId] = useState("p-send");
  const [chainId, setChainId] = useState("provider");
  const [org, setOrg] = useState("acme");
  const [subject, setSubject] = useState("svc:bot");
  // details
  const [granter, setGranter] = useState("");
  const [target, setTarget] = useState("");
  const [amount, setAmount] = useState(""); // ATOM
  const [proposalId, setProposalId] = useState("42");
  const [voteOption, setVoteOption] = useState<string>("yes");

  const [result, setResult] = useState<Intent | null>(null);
  const [err, setErr] = useState("");

  const isSend = action === "bank.send";
  const isStake = action === "staking.delegate";
  const isVote = action === "gov.vote";
  const isWithdraw = action === "distribution.withdrawRewards";
  const hasAmount = isSend || isStake;
  const hasTarget = isSend || isStake || isWithdraw;
  const targetLabel = isSend ? "Recipient address" : isStake ? "Validator" : "Validator (to withdraw from)";

  const intent = useMemo(() => {
    const params: Record<string, unknown> = { granter: granter.trim() };
    if (isVote) {
      params.proposalId = Number(proposalId) || 0;
      params.voteOption = voteOption;
    } else if (hasAmount) {
      params.target = target.trim();
      params.amount = { denom: "uatom", amount: atomToUatom(amount) ?? "0" };
    } else if (isWithdraw) {
      params.target = target.trim();
    }
    return { org: org.trim(), policyId: policyId.trim(), subject: subject.trim(), action, chainId: chainId.trim(), params };
  }, [org, policyId, subject, action, chainId, granter, proposalId, voteOption, target, amount, isVote, hasAmount, isWithdraw]);

  // plain-language "what you're asking for"
  const sentence = useMemo(() => {
    const where = chainId.trim() ? ` on ${chainId.trim()}` : "";
    const who = granter.trim() || "the treasury account";
    if (isSend) return `Send ${amount || "?"} ATOM from ${who} to ${target.trim() || "?"}${where}.`;
    if (isStake) return `Delegate ${amount || "?"} ATOM from ${who} to validator ${target.trim() || "?"}${where}.`;
    if (isVote) return `Vote “${voteOption}” on proposal #${proposalId} as ${who}${where}.`;
    return `Withdraw staking rewards for ${who} from ${target.trim() || "?"}${where}.`;
  }, [isSend, isStake, isVote, amount, granter, target, voteOption, proposalId, chainId]);

  async function submit() {
    setErr("");
    setResult(null);
    try {
      const r = await api.submitIntent({ ...intent, idempotencyKey: "ui-" + crypto.randomUUID() });
      setResult(r);
      if (r.status === "awaiting_approval") setApproveId(r.id);
      setStep(3);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }
  function reset() {
    setResult(null);
    setErr("");
    setStep(0);
  }

  // --- lookup ---
  const [lookupId, setLookupId] = useState("");
  const [found, setFound] = useState<Intent | null>(null);
  const [lookupErr, setLookupErr] = useState("");
  async function lookup() {
    setLookupErr("");
    setFound(null);
    try {
      setFound(await api.getIntent(lookupId.trim()));
    } catch (e) {
      setLookupErr(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }

  // --- approve ---
  const [approveId, setApproveId] = useState("");
  const [by, setBy] = useState("");
  const [signature, setSignature] = useState("");
  const [approveMsg, setApproveMsg] = useState("");
  const [approveErr, setApproveErr] = useState("");
  const [keplrPubkey, setKeplrPubkey] = useState("");
  const [keplrBusy, setKeplrBusy] = useState(false);

  async function approve() {
    setApproveErr("");
    setApproveMsg("");
    try {
      const updated = await api.approveIntent(approveId.trim(), { by: by.trim(), signature: signature.trim() });
      setApproveMsg(`OK - ${updated.status} (${updated.approvals.length}/${updated.requiredApprovals})`);
    } catch (e) {
      setApproveErr(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }
  async function approveKeplr() {
    setApproveErr("");
    setApproveMsg("");
    setKeplrPubkey("");
    if (!approveId.trim() || !by.trim()) {
      setApproveErr("Enter Intent id and Approver before signing with Keplr.");
      return;
    }
    setKeplrBusy(true);
    try {
      const { message } = await api.approvalMessage(approveId.trim(), by.trim());
      const signed = await keplrSignApproval(message);
      setKeplrPubkey(signed.pubkeyB64);
      const updated = await api.approveIntent(approveId.trim(), { by: by.trim(), signature: signed.signatureB64, scheme: "secp256k1" });
      setApproveMsg(`OK (Keplr ${signed.address.slice(0, 12)}…) - ${updated.status} (${updated.approvals.length}/${updated.requiredApprovals})`);
    } catch (e) {
      setApproveErr(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    } finally {
      setKeplrBusy(false);
    }
  }

  return (
    <>
      <h2>Intents</h2>

      <div className="card">
        <h3>Do something - in 3 steps</h3>
        <p className="muted small">An intent is a request to perform an action. Praxis checks your policy + the on-chain grant, then runs it or explains why not.</p>

        <div className="steps">
          {STEPS.map((label, i) => (
            <button key={label} type="button" className={`step${i === step ? " active" : ""}${i < step ? " done" : ""}`} onClick={() => i < 3 && setStep(i)}>
              <span className="n">{i < step ? "✓" : i + 1}</span>
              {label}
            </button>
          ))}
        </div>

        {/* STEP 1 - what */}
        {step === 0 && (
          <>
            <label>
              What do you want to do?
              <select value={action} onChange={(e) => setAction(e.target.value as ActionKey)}>
                {ACTIONS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="row">
              <label>
                Under which policy (id)
                <input value={policyId} onChange={(e) => setPolicyId(e.target.value)} placeholder="p-send" />
              </label>
              <label>
                Network
                <input value={chainId} onChange={(e) => setChainId(e.target.value)} placeholder="provider" />
              </label>
            </div>
            <p className="hint">The policy is the rulebook this action must obey. The network is the chain id (e.g. provider, cosmoshub-4).</p>
            <details>
              <summary className="muted small">Advanced - org &amp; subject</summary>
              <div className="row">
                <label>
                  Org
                  <input value={org} onChange={(e) => setOrg(e.target.value)} />
                </label>
                <label>
                  Subject (operator key id)
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="svc:bot" />
                </label>
              </div>
            </details>
          </>
        )}

        {/* STEP 2 - details */}
        {step === 1 && (
          <>
            <label>
              Account acted on (the treasury that granted the operator)
              <input value={granter} onChange={(e) => setGranter(e.target.value)} placeholder="cosmos1… (granter)" />
            </label>
            {hasTarget && (
              <label>
                {targetLabel}
                <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder={isSend ? "cosmos1…" : "cosmosvaloper1…"} />
              </label>
            )}
            {hasAmount && (
              <>
                <label>
                  Amount (ATOM)
                  <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 0.05" inputMode="decimal" />
                </label>
                <p className="hint">{atomToUatom(amount) ? `= ${atomToUatom(amount)} uatom (what the chain sees)` : "Enter an amount in ATOM."}</p>
              </>
            )}
            {isVote && (
              <div className="row">
                <label>
                  Proposal id
                  <input value={proposalId} onChange={(e) => setProposalId(e.target.value)} type="number" />
                </label>
                <label>
                  Your vote
                  <select value={voteOption} onChange={(e) => setVoteOption(e.target.value)}>
                    {VOTE_OPTIONS.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </>
        )}

        {/* STEP 3 - review */}
        {step === 2 && (
          <>
            <h3>Review</h3>
            <div className="summary">
              <p style={{ margin: 0 }}>
                📨 {sentence}
              </p>
            </div>
            <p className="hint">Praxis will check this against policy “{policyId}” and the on-chain grant, then execute or deny.</p>
            {err && <div className="err">{err}</div>}
            <button onClick={() => void submit()}>Submit intent</button>
            <details style={{ marginTop: 10 }}>
              <summary className="muted small">Advanced - JSON preview</summary>
              <pre>{JSON.stringify(intent, null, 2)}</pre>
            </details>
          </>
        )}

        {/* STEP 4 - result */}
        {step === 3 && (
          <>
            <h3>Result</h3>
            {err && <div className="err">{err}</div>}
            {result && (
              <>
                <p>
                  <span className={`badge ${statusBadge(result.status)}`}>{result.status}</span>{" "}
                  {result.txHash && <code className="small">tx {result.txHash.slice(0, 20)}…</code>}
                </p>
                {result.status === "executed" && <p className="muted">✅ Done - the action was signed by the operator and broadcast on-chain.</p>}
                {result.status === "awaiting_approval" && (
                  <p className="warn-text">
                    ⏳ Waiting for approvals ({result.approvals.length}/{result.requiredApprovals}). Approve it in the “Approve an intent” card below - it’s pre-filled with this intent id.
                  </p>
                )}
                {statusBadge(result.status) === "bad" && (
                  <p className="err" style={{ marginTop: 6 }}>
                    ❌ Not executed{result.failureReason ? ` - ${result.failureReason}` : ""}. Adjust the policy or the request and try again.
                  </p>
                )}
                <details style={{ marginTop: 8 }}>
                  <summary className="muted small">Full response</summary>
                  <pre>{JSON.stringify(result, null, 2)}</pre>
                </details>
              </>
            )}
            <button className="ghost" onClick={reset} style={{ marginTop: 8 }}>
              ＋ New intent
            </button>
          </>
        )}

        {step < 2 && (
          <div className="wizard-nav">
            <button type="button" className="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
              ← Back
            </button>
            <button type="button" onClick={() => setStep((s) => Math.min(2, s + 1))}>
              Next →
            </button>
          </div>
        )}
        {step === 2 && (
          <div className="wizard-nav">
            <button type="button" className="ghost" onClick={() => setStep(1)}>
              ← Back
            </button>
            <span />
          </div>
        )}
      </div>

      <div className="card">
        <h3>Look up an intent</h3>
        <div className="row">
          <label style={{ margin: 0 }}>
            Intent id
            <input value={lookupId} onChange={(e) => setLookupId(e.target.value)} placeholder="uuid" />
          </label>
          <button onClick={() => void lookup()}>Fetch</button>
        </div>
        {lookupErr && <div className="err">{lookupErr}</div>}
        {found && (
          <>
            <p style={{ marginTop: 10 }}>
              <span className={`badge ${statusBadge(found.status)}`}>{found.status}</span>
            </p>
            <pre>{JSON.stringify(found, null, 2)}</pre>
          </>
        )}
      </div>

      <div className="card">
        <h3>Approve an intent (m-of-n)</h3>
        <p className="muted small">
          Sign with your <b>Keplr / Ledger</b> wallet (recommended) - verified server-side against your registered key.
        </p>
        <div className="row">
          <label>
            Intent id
            <input value={approveId} onChange={(e) => setApproveId(e.target.value)} placeholder="uuid" />
          </label>
          <label>
            Approver (subject id)
            <input value={by} onChange={(e) => setBy(e.target.value)} placeholder="member:alice" />
          </label>
        </div>
        <button onClick={() => void approveKeplr()} disabled={keplrBusy}>
          {keplrBusy ? "Signing…" : "Approve with Keplr"}
        </button>
        {keplrPubkey && (
          <p className="muted small" style={{ marginTop: 10 }}>
            Signer pubkey (register as the subject’s <code>publicKey</code>, scheme <code>secp256k1</code>):<br />
            <code className="mono">{keplrPubkey}</code>
          </p>
        )}
        <details style={{ marginTop: 10 }}>
          <summary className="muted small">Manual signature (CLI / advanced)</summary>
          <label>
            Signature (base64)
            <input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="base64 signature" />
          </label>
          <button className="ghost" onClick={() => void approve()}>
            Submit pasted signature
          </button>
        </details>
        {approveErr && <div className="err">{approveErr}</div>}
        {approveMsg && (
          <p className="badge ok" style={{ marginTop: 10 }}>
            {approveMsg}
          </p>
        )}
      </div>
    </>
  );
}
